import { useState } from "react";
import { Minus, Plus, X, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { api, errorText, formatPrice } from "@/lib/api";

export function CartDrawer() {
  const { items, setQty, total, open, setOpen } = useCart();
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    setBusy(true);
    try {
      const res = await api.post("/payments/checkout", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(errorText(e));
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" side="right" className="w-full sm:max-w-md border-l border-white/10 bg-[#0d0d10] p-0 text-white [&>button]:hidden" data-lenis-prevent>
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
            <div>
              <SheetTitle className="font-display text-3xl font-extrabold uppercase text-white">Your Loadout</SheetTitle>
              <SheetDescription className="font-mono text-xs uppercase tracking-widest text-zinc-400">{items.length} line items</SheetDescription>
            </div>
            <button data-testid="cart-close-button" onClick={() => setOpen(false)} className="grid h-10 w-10 place-items-center border border-white/15 hover:border-[var(--volt)]">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {items.length === 0 && (
              <p data-testid="cart-empty" className="mt-16 text-center font-mono text-sm uppercase tracking-widest text-zinc-400">Cart is empty. Gear up.</p>
            )}
            {items.map((i) => (
              <div key={i.slug} data-testid={`cart-item-${i.slug}`} className="flex gap-4 border-b border-white/10 py-4">
                <div className="h-20 w-24 shrink-0 bg-white">
                  {i.image && <img src={i.image} alt={i.name} className="h-full w-full object-contain" />}
                </div>
                <div className="flex flex-1 flex-col">
                  <p className="font-display text-xl font-bold uppercase leading-tight">{i.name}</p>
                  <p className="font-mono text-[11px] text-zinc-400">{i.model}</p>
                  <div className="mt-auto flex items-center justify-between">
                    <div className="flex items-center border border-white/15">
                      <button data-testid={`cart-dec-${i.slug}`} onClick={() => setQty(i.slug, i.quantity - 1)} className="grid h-8 w-8 place-items-center hover:bg-white/10"><Minus className="h-3 w-3" /></button>
                      <span className="w-8 text-center font-mono text-sm">{i.quantity}</span>
                      <button data-testid={`cart-inc-${i.slug}`} onClick={() => setQty(i.slug, i.quantity + 1)} className="grid h-8 w-8 place-items-center hover:bg-white/10"><Plus className="h-3 w-3" /></button>
                    </div>
                    <span className="font-mono text-sm text-volt">{formatPrice((i.price || 0) * i.quantity)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/10 px-6 py-6">
            <div className="mb-1 flex items-baseline justify-between">
              <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">Subtotal</span>
              <span data-testid="cart-subtotal" className="font-display text-3xl font-extrabold">{formatPrice(total)}</span>
            </div>
            <p className="mb-5 font-mono text-[11px] text-zinc-400">Shipping and tax calculated at checkout.</p>
            <button
              data-testid="cart-checkout-button"
              disabled={!items.length || busy}
              onClick={checkout}
              className="btn-volt flex h-14 w-full items-center justify-center gap-3 font-mono text-sm font-bold uppercase tracking-widest disabled:opacity-40"
            >
              <span className="flex items-center gap-3">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Secure Checkout <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
